/**
 * DME-CPH Application Constants
 * =============================
 * Centralized constants for the application.
 */

// ========================================
// BRAND COLORS (for JS usage)
// ========================================
export const DME_COLORS = {
  // Primary
  primaryBlue: '#1B3A6B',
  accentOrange: '#2323FF',
  successGreen: '#28A745',
  
  // Extended Blues
  blue50: '#E8F4FD',
  blue100: '#C5E1F9',
  blue200: '#9ECCF4',
  blue300: '#77B7EF',
  blue400: '#5AA7EB',
  blue500: '#3D97E7',
  blue600: '#1B3A6B',
  blue700: '#152D54',
  blue800: '#0F1F3A',
  blue900: '#091220',
  
  // Extended Oranges
  orange50: '#FFF3E8',
  orange100: '#FFE2C7',
  orange200: '#FFCEA3',
  orange300: '#FFBA7F',
  orange400: '#FFAB63',
  orange500: '#FF9D47',
  orange600: '#E87722',
  orange700: '#D66A1A',
  orange800: '#B85815',
  orange900: '#8A4210',
  
  // Extended Greens
  green50: '#E8F5E9',
  green100: '#C8E6C9',
  green200: '#A5D6A7',
  green300: '#81C784',
  green400: '#66BB6A',
  green500: '#4CAF50',
  green600: '#28A745',
  green700: '#218838',
  green800: '#1C7430',
  green900: '#155A25',
  
  // Grays
  gray50: '#F8F9FA',
  gray100: '#F1F3F4',
  gray200: '#E8EAED',
  gray300: '#DADCE0',
  gray400: '#9AA0A6',
  gray500: '#5F6368',
  gray600: '#3C4043',
  gray700: '#202124',
  gray800: '#171717',
  gray900: '#0A0A0A',
  
  // Semantic
  error: '#DC3545',
  warning: '#FFC107',
  info: '#17A2B8',
};

// ========================================
// NAVIGATION LINKS
// ========================================
export const NAV_LINKS = {
  public: [
    { label: 'Home', path: '/', icon: 'Home' },
    { label: 'Features', path: '/features', icon: 'Sparkles' },
    { label: 'Integrations', path: '/integrations', icon: 'Plug' },
    { label: 'How It Works', path: '/how-it-works', icon: 'Workflow' },
    { label: 'Results', path: '/results', icon: 'TrendingUp' },
    { label: 'FAQ', path: '/faq', icon: 'HelpCircle' },
    { label: 'Contact', path: '/contact', icon: 'Mail' },
  ],
  protected: [
    { label: 'Demo', path: '/demo', icon: 'Play' },
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
  ],
  auth: [
    { label: 'Login', path: '/login', icon: 'LogIn' },
  ],
};

// ========================================
// AUTHENTICATION
// ========================================
export const AUTH_CONFIG = {
  // Demo credentials for protected routes
  DEMO_USERNAME: 'demo',
  DEMO_PASSWORD: 'dme2026',
  
  // Storage key
  STORAGE_KEY: 'auth',
  
  // Routes
  LOGIN_PATH: '/login',
  DASHBOARD_PATH: '/dashboard',
  DEMO_PATH: '/demo',

  // After login, send users to the working Addi dashboard (keep /dashboard route for layout reference)
  DASHBOARD_EXTERNAL_URL: 'https://addi.vaultailab.com',
};

// ========================================
// GSAP SCROLL TRIGGER CONFIG
// ========================================
export const SCROLL_CONFIG = {
  // Default trigger settings
  defaults: {
    markers: false,
    scrub: 1,
    anticipatePin: 1,
  },
  
  // Viewport positions
  viewport: {
    top: 'top 0%',
    center: 'top 50%',
    bottom: 'top 100%',
    '80%': 'top 80%',
    '20%': 'top 20%',
  },
  
  // Easing presets
  easing: {
    smooth: 'power2.inOut',
    bounce: 'back.out(1.7)',
    elastic: 'elastic.out(1, 0.5)',
    expo: 'expo.out',
    circ: 'circ.out',
  },
  
  // Durations
  duration: {
    fast: 0.3,
    normal: 0.6,
    slow: 1.0,
    cinematic: 1.5,
  },
};

// ========================================
// ANIMATION TIMING
// ========================================
export const ANIMATION_TIMING = {
  // Stagger delays
  stagger: {
    fast: 0.05,
    normal: 0.1,
    slow: 0.15,
    cascade: 0.2,
  },
  
  // Section scroll distances
  scrollDistance: {
    hero: '150vh',
    problem: '200vh',
    solution: '250vh',
    proof: '300vh',
    cta: '100vh',
  },
  
  // Pin durations (in viewport height)
  pinDuration: {
    hero: 1.5,
    problem: 2,
    solution: 2.5,
    proof: 3,
    cta: 1,
  },
};

// ========================================
// BREAKPOINTS
// ========================================
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

// ========================================
// IMAGE SEQUENCE CONFIG (ADDI Demo)
// ========================================
export const IMAGE_SEQUENCE_CONFIG = {
  // Frame settings
  frameCount: 240,
  framePrefix: 'Stetson-Hero-',
  framePadding: 3, // Number of digits (001, 002, etc.)
  frameExtension: 'jpg',
  
  // Paths
  basePath: '/assets/image-sequence/',
  
  // Scroll settings
  scrollDistance: '400vh',
  
  // Canvas settings
  canvasWidth: 1920,
  canvasHeight: 1080,
};

// ========================================
// DASHBOARD CONFIG
// ========================================
export const DASHBOARD_CONFIG = {
  // Refresh intervals (in milliseconds)
  refreshInterval: {
    realtime: 5000,
    frequent: 30000,
    normal: 60000,
    hourly: 3600000,
  },
  
  // Chart colors
  chartColors: {
    primary: DME_COLORS.primaryBlue,
    secondary: DME_COLORS.accentOrange,
    success: DME_COLORS.successGreen,
    warning: DME_COLORS.warning,
    error: DME_COLORS.error,
    info: DME_COLORS.info,
  },
  
  // Date ranges
  dateRanges: [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Last 7 Days', value: '7d' },
    { label: 'Last 30 Days', value: '30d' },
    { label: 'This Month', value: 'month' },
    { label: 'Last Month', value: 'lastMonth' },
    { label: 'This Quarter', value: 'quarter' },
    { label: 'This Year', value: 'year' },
  ],
};

// ========================================
// META INFORMATION
// ========================================
export const SITE_META = {
  title: 'DME-CPH | Data-Driven Enrollment Marketing',
  description: 'Transform your enrollment strategy with predictive analytics and AI-powered insights.',
  keywords: 'enrollment marketing, higher education, predictive analytics, student recruitment, DME, CPH',
  author: 'DME-CPH Team',
  ogImage: '/assets/images/og-image.png',
  twitterHandle: '@dmecph',
};

// ========================================
// CONTACT INFORMATION
// ========================================
export const CONTACT_INFO = {
  email: 'info@dmecph.com',
  phone: '(555) 123-4567',
  address: {
    street: '123 Education Lane',
    city: 'University City',
    state: 'CA',
    zip: '90210',
  },
  social: {
    linkedin: 'https://linkedin.com/company/dme-cph',
    twitter: 'https://twitter.com/dmecph',
    facebook: 'https://facebook.com/dmecph',
  },
};

// ========================================
// FEATURE HIGHLIGHTS
// ========================================
export const FEATURES = [
  {
    id: 'predictive-analytics',
    title: 'Predictive Analytics',
    description: 'Identify at-risk students before they drop out with AI-powered predictions.',
    icon: 'Brain',
    color: 'blue',
  },
  {
    id: 'enrollment-forecasting',
    title: 'Enrollment Forecasting',
    description: 'Predict enrollment numbers with 95%+ accuracy using historical data.',
    icon: 'TrendingUp',
    color: 'orange',
  },
  {
    id: 'real-time-dashboard',
    title: 'Real-Time Dashboard',
    description: 'Monitor key metrics in real-time with customizable visualizations.',
    icon: 'LayoutDashboard',
    color: 'green',
  },
  {
    id: 'automated-reporting',
    title: 'Automated Reporting',
    description: 'Generate comprehensive reports automatically and schedule deliveries.',
    icon: 'FileText',
    color: 'blue',
  },
  {
    id: 'integration-hub',
    title: 'Integration Hub',
    description: 'Connect with your existing systems: SIS, CRM, LMS, and more.',
    icon: 'Plug',
    color: 'orange',
  },
  {
    id: 'ai-insights',
    title: 'AI Insights',
    description: 'Get actionable recommendations powered by machine learning.',
    icon: 'Sparkles',
    color: 'green',
  },
];

// ========================================
// STATS FOR RESULTS PAGE
// ========================================
export const RESULTS_STATS = [
  { value: 95, suffix: '%', label: 'Prediction Accuracy', description: 'Enrollment forecasting precision' },
  { value: 40, suffix: '%', label: 'Retention Improvement', description: 'Average increase in student retention' },
  { value: 25, suffix: '%', label: 'Marketing Efficiency', description: 'Reduction in cost per enrollment' },
  { value: 150, suffix: '+', label: 'Partner Institutions', description: 'Universities and colleges served' },
];

// ========================================
// INTEGRATION PARTNERS
// ========================================
export const INTEGRATION_PARTNERS = [
  { name: 'Salesforce', category: 'CRM', status: 'available' },
  { name: 'HubSpot', category: 'CRM', status: 'available' },
  { name: 'Ellucian', category: 'SIS', status: 'available' },
  { name: 'Banner', category: 'SIS', status: 'available' },
  { name: 'Canvas', category: 'LMS', status: 'available' },
  { name: 'Blackboard', category: 'LMS', status: 'available' },
  { name: 'Moodle', category: 'LMS', status: 'available' },
  { name: 'Google Analytics', category: 'Analytics', status: 'available' },
  { name: 'Tableau', category: 'Analytics', status: 'coming-soon' },
  { name: 'Power BI', category: 'Analytics', status: 'coming-soon' },
];

// ========================================
// FAQ ITEMS
// ========================================
export const FAQ_ITEMS = [
  {
    question: 'What is DME-CPH and how does it work?',
    answer: 'DME-CPH is a data-driven enrollment marketing platform that uses predictive analytics and AI to help higher education institutions optimize their recruitment and retention strategies. Our platform integrates with your existing systems to provide real-time insights and actionable recommendations.',
  },
  {
    question: 'How long does implementation typically take?',
    answer: 'Implementation typically takes 4-6 weeks, depending on the complexity of your existing systems and data sources. Our team provides comprehensive onboarding and training to ensure a smooth transition.',
  },
  {
    question: 'What systems can DME-CPH integrate with?',
    answer: 'DME-CPH integrates with major CRM platforms (Salesforce, HubSpot), Student Information Systems (Ellucian, Banner), Learning Management Systems (Canvas, Blackboard, Moodle), and analytics tools (Google Analytics). We also offer custom API integrations.',
  },
  {
    question: 'Is my data secure with DME-CPH?',
    answer: 'Absolutely. We use industry-standard encryption, comply with FERPA regulations, and maintain SOC 2 Type II certification. Your data is stored in secure, redundant data centers with 99.9% uptime guarantee.',
  },
  {
    question: 'What kind of support do you offer?',
    answer: 'We offer 24/7 technical support, dedicated account managers, regular check-ins, and comprehensive documentation. Enterprise clients also receive priority support and custom development options.',
  },
  {
    question: 'Can I try DME-CPH before committing?',
    answer: 'Yes! We offer a 30-day free trial with full access to all features. You can also request a personalized demo to see how DME-CPH can work for your specific needs.',
  },
];

// ========================================
// HOW IT WORKS STEPS
// ========================================
export const HOW_IT_WORKS_STEPS = [
  {
    number: '01',
    title: 'Connect Your Data',
    description: 'Integrate DME-CPH with your existing systems in minutes. We support all major CRM, SIS, and LMS platforms.',
    icon: 'Plug',
  },
  {
    number: '02',
    title: 'AI Analysis',
    description: 'Our machine learning algorithms analyze your historical data to identify patterns and predict future outcomes.',
    icon: 'Brain',
  },
  {
    number: '03',
    title: 'Get Insights',
    description: 'Receive actionable recommendations and real-time dashboards that help you make data-driven decisions.',
    icon: 'Lightbulb',
  },
  {
    number: '04',
    title: 'Optimize Results',
    description: 'Implement recommendations and watch your enrollment numbers and retention rates improve.',
    icon: 'Rocket',
  },
];
